import React, { useState } from "react";
import { MapPin, Phone, Mail, MessageCircle, Clock, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import SEO, { breadcrumbSchema } from "@/components/SEO";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", website: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact Titan Car Rentals — East Legon, Accra · Ghana"
        description="Reach Titan Car Rentals: 3 Maseru St., East Legon, Accra. Phone 054 012 4247 / 020 123 4999, WhatsApp 024 494 5582, email info@titancarrental.com. 24/7 chat available."
        path="/contact"
        jsonLd={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }])}
      />
      <section className="bg-[#0d2040] text-white py-20">
        <div className="container-titan">
          <span className="eyebrow text-[#ff8a93]">Get in touch</span>
          <h1 className="h-section mt-3 text-white">We&apos;re right here in East Legon.</h1>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-titan grid lg:grid-cols-12 gap-10">
          {/* Info */}
          <div className="lg:col-span-5">
            <h2 className="font-display text-3xl font-semibold text-[#0d2040]">Reach Titan Car Rentals</h2>
            <p className="text-gray-600 mt-3">
              Walk in to our HQ on Maseru Street any day of the week, or get in touch online.
            </p>

            <ul className="mt-8 space-y-5">
              <li className="flex gap-4">
                <div className="w-10 h-10 bg-[#0d2040] text-white flex items-center justify-center flex-shrink-0"><MapPin size={16} /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-gray-500">Address</div>
                  <div className="text-[#0d2040] font-medium">3 Maseru St., East Legon · Accra, Ghana</div>
                </div>
              </li>
              <li className="flex gap-4">
                <div className="w-10 h-10 bg-[#0d2040] text-white flex items-center justify-center flex-shrink-0"><Phone size={16} /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-gray-500">Telephone</div>
                  <a href="tel:+233540124247" className="block text-[#0d2040] font-medium hover:text-[#e63946]" data-testid="contact-phone-1">054 012 4247</a>
                  <a href="tel:+233201234999" className="block text-[#0d2040] font-medium hover:text-[#e63946]" data-testid="contact-phone-2">020 123 4999</a>
                </div>
              </li>
              <li className="flex gap-4">
                <div className="w-10 h-10 bg-[#0d2040] text-white flex items-center justify-center flex-shrink-0"><Mail size={16} /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-gray-500">Email</div>
                  <a href="mailto:info@titancarrental.com" className="text-[#0d2040] font-medium hover:text-[#e63946]">info@titancarrental.com</a>
                </div>
              </li>
              <li className="flex gap-4">
                <div className="w-10 h-10 bg-[#25D366] text-white flex items-center justify-center flex-shrink-0"><MessageCircle size={16} /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-gray-500">WhatsApp</div>
                  <a href="https://wa.me/233244945582" target="_blank" rel="noreferrer noopener" className="text-[#0d2040] font-medium hover:text-[#e63946]" data-testid="contact-whatsapp">
                    Click for WhatsApp Chat
                  </a>
                </div>
              </li>
              <li className="flex gap-4">
                <div className="w-10 h-10 bg-[#0d2040] text-white flex items-center justify-center flex-shrink-0"><Clock size={16} /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-gray-500">Hours</div>
                  <div className="text-[#0d2040] font-medium">Mon – Sat &nbsp; 7:00 – 20:00<br />Sun &nbsp; 9:00 – 18:00</div>
                </div>
              </li>
            </ul>

            <div className="mt-8 aspect-[5/3] bg-gray-100 overflow-hidden border border-gray-200">
              <iframe
                title="Titan Car Rentals on map"
                src="https://www.google.com/maps?q=East%20Legon%20Accra%20Ghana&output=embed"
                className="w-full h-full"
                loading="lazy"
              />
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <form onSubmit={submit} className="bg-white border border-gray-200 p-7 booking-widget" data-testid="contact-form">
              {/* Honeypot */}
              <input
                type="text"
                name="website"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
              />
              <h3 className="font-display text-2xl font-semibold text-[#0d2040]">Send us a message</h3>
              <p className="text-sm text-gray-600 mt-1">We typically reply within a few hours.</p>

              <div className="grid md:grid-cols-2 gap-4 mt-6">
                <div>
                  <label className="titan-label">Full name *</label>
                  <input className="titan-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="contact-name" />
                </div>
                <div>
                  <label className="titan-label">Email *</label>
                  <input type="email" className="titan-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="contact-email" />
                </div>
                <div>
                  <label className="titan-label">Phone</label>
                  <input className="titan-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone-input" />
                </div>
                <div>
                  <label className="titan-label">Subject</label>
                  <input className="titan-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" />
                </div>
                <div className="md:col-span-2">
                  <label className="titan-label">Message *</label>
                  <textarea rows={6} className="titan-input" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required data-testid="contact-message" />
                </div>
              </div>
              <button type="submit" disabled={submitting} className="btn-primary mt-5" data-testid="contact-submit-btn">
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <><Send size={14} /> Send message</>}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
